import { useEffect, useRef, useState, type ReactNode } from "react";
import {
    Reply, ReplyAll, Forward, Trash2, Archive, Flag, HardDrive, Paperclip, Pin, PinOff, Download,
    ExternalLink, ListTodo, Languages, Loader2, Mail, MailOpen, Printer, ShieldAlert, MailMinus,
    ChevronDown, MoreHorizontal, Sparkles, X, Copy, Check, CheckCircle2, Info, CloudUpload
} from "lucide-react";
import { useAuth } from "react-oidc-context";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
    DropdownMenuSeparator, DropdownMenuSub, DropdownMenuSubContent, DropdownMenuSubTrigger, DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { useStatusAnnouncer } from "@/components/StatusAnnouncer";
import { getInitials, cn } from "@/lib/utils";
import {
    mailApi, type MailAddress, type Message, type MessageAttachment, type MessageCategory, type MessageSummary
} from "@/api/mailApi";
import { useAccessToken } from "@/auth/useAccessToken";
import {
    useMessageAction, useThreadByMessage, useSendReadReceipt, useDismissReadReceipt,
    useTranslateSegments, useAiChat, useSafeSenders, useAddSafeSender
} from "@/api/hooks";
import { useCreateTaskFromMessage, useTaskTargets } from "@/features/tasks/fromMessage";
import { LabelChips, LabelPicker } from "./LabelPicker";
import { SnoozeMenu } from "./SnoozeMenu";
import { RecipientChip } from "./RecipientChip";
import { EmailBodyFrame } from "./EmailBodyFrame";
import { MeetingJoinCard } from "./MeetingJoinCard";
import { avatarColorClass } from "./avatarColor";
import { categoryTheme, type CategoryTheme } from "./categoryTheme";
import { translateEmailHtml } from "./translateHtml";
import { buildOpenInUrl, resolveOpenInTarget } from "./openInSuite";
import { PdfPreviewDialog } from "./PdfPreviewDialog";
import { canQuickView } from "./quickView";
import { DriveFilePicker } from "./DriveFilePicker";
import { DriveLinkCards } from "./DriveLinkCards";
import { driveApi, driveFolderUrl, driveOpenUrl, isDriveConfigured, type DriveItem } from "@/api/driveApi";

const driveEnabled = isDriveConfigured();

const baseUrl = import.meta.env.VITE_MAIL_BFF_BASE_URL;

/**
 * Content that loads from the internet when the email is shown — remote
 * images (src/srcset/background), CSS url(…) and <link> stylesheets. Plain
 * links (<a href>) are not remote content and never trigger the block bar.
 */
export const REMOTE_CONTENT =
    /\b(?:src|srcset|background)\s*=\s*["']?\s*(?:https?:)?\/\/|url\(\s*["']?\s*(?:https?:)?\/\/|<link\b[^>]*\bhref\s*=\s*["']?\s*(?:https?:)?\/\//i;

function sliceBeforeQuote(html: string): string {
    const i = findQuoteBoundary(html);
    return i === -1 ? html : html.slice(0, i);
}

/**
 * Finds the byte offset in an HTML body where quoted-reply / forwarded history
 * begins, so the reading pane can collapse it behind the "···" toggle the way
 * Gmail does. Matches the wrapper each major client emits, at a tag boundary so
 * a split never lands mid-tag:
 *   - Gmail:       <div class="gmail_quote"> / <div class="gmail_attr">
 *                  (covers both "On … wrote:" replies and "---- Forwarded
 *                  message ----" forwards, which Gmail nests in gmail_quote)
 *   - Outlook:     <div id="appendonsend"> / <div id="divRplyFwdMsg">
 *   - Thunderbird: <div class="moz-cite-prefix">
 *   - Generic:     a bare <blockquote>
 * Returns the earliest match's index, or -1 when there's no quoted history.
 */
export function findQuoteBoundary(html: string): number {
    const marker =
        /<blockquote|<div[^>]*class="[^"]*gmail_quote|<div[^>]*class="[^"]*gmail_attr|<div[^>]*class="[^"]*moz-cite-prefix|<div[^>]*id="?(?:appendonsend|divRplyFwdMsg)/i;
    const m = marker.exec(html);
    return m ? m.index : -1;
}

// ── Shared building blocks (also used by ConversationView) ──────────────────
// The approved page-1 mock-up: one white card per reader, a sticky subject
// header, and each message as a bordered block whose body, attachments and
// reply buttons line up in a column 68 px from the left (past the avatar).

/** Icon-only action in the reading pane (30 px, as in the mock-up). */
const ICON_BTN = "h-[30px] w-[30px] text-foreground/75 hover:bg-[var(--mail-hover)] hover:text-foreground";

/** The column everything under a message header lines up in. */
const COLUMN = "pl-4 pr-4 sm:pl-[68px]";

export function IconButton({
    label,
    onClick,
    disabled,
    className,
    children
}: {
    label: string;
    onClick?: () => void;
    disabled?: boolean;
    className?: string;
    children: ReactNode;
}) {
    return (
        <Button
            variant="ghost"
            size="icon"
            className={cn(ICON_BTN, className)}
            aria-label={label}
            title={label}
            disabled={disabled}
            onClick={onClick}
        >
            {children}
        </Button>
    );
}

/** Solid category pill — the same colour as the message list's. */
function categorySolidClass(category: MessageCategory): string {
    const theme = categoryTheme[category] as CategoryTheme & { solid?: string };
    return theme.solid ?? `${theme.dot} text-white`;
}

/** Category pill for the subject line. "Updates" is the inbox tab, so it gets no pill. */
export function CategoryChip({ category }: { category: MessageCategory | null | undefined }) {
    if (!category || category === "updates") return null;
    const theme = categoryTheme[category];
    if (!theme) return null;
    return (
        <span className={cn("inline-flex h-5 shrink-0 items-center rounded px-[7px] text-[11px] font-semibold", categorySolidClass(category))}>
            {theme.label}
        </span>
    );
}

/** Wraps a reader: the card on the mail canvas, scrolling as one. */
export function ReaderShell({ label, children }: { label: string; children: ReactNode }) {
    return (
        <section
            className="flex h-full min-w-0 flex-1 flex-col overflow-hidden focus:outline-none"
            aria-label={label}
            tabIndex={-1}
            data-reading-pane=""
        >
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg bg-card shadow-[var(--mail-shadow-4)]">
                <div className="min-h-0 flex-1 overflow-y-auto pb-8">{children}</div>
            </div>
        </section>
    );
}

/** Sticky subject header: subject, categories on the same line, then the tools. */
export function SubjectHeader({
    subject,
    meta,
    chips,
    tools
}: {
    subject: string;
    meta?: ReactNode;
    chips?: ReactNode;
    tools?: ReactNode;
}) {
    return (
        <header className="sticky top-0 z-10 flex flex-wrap items-center gap-2.5 border-b border-[var(--mail-line)] bg-card px-5 pb-3 pt-3.5">
            <div className="min-w-0 flex-1 basis-[280px]">
                <h1
                    className="line-clamp-3 break-words text-[20px] font-semibold leading-[26px] text-foreground [text-wrap:balance]"
                    title={subject}
                >
                    {subject || "(no subject)"}
                </h1>
                {meta ? <p className="mt-0.5 truncate text-xs text-[var(--mail-text-2)]">{meta}</p> : null}
            </div>
            {chips ? <div className="flex flex-wrap items-center gap-1.5 empty:hidden">{chips}</div> : null}
            {tools ? <div className="flex shrink-0 items-center gap-1">{tools}</div> : null}
        </header>
    );
}

export function SenderAvatar({
    address,
    className,
    textClassName
}: {
    address: MailAddress;
    className?: string;
    textClassName?: string;
}) {
    return (
        <Avatar className={cn("h-8 w-8 shrink-0", className)}>
            <AvatarFallback className={cn("text-xs font-semibold", textClassName, avatarColorClass(address.email))}>
                {getInitials(address.name || address.email)}
            </AvatarFallback>
        </Avatar>
    );
}

export function ExternalTag() {
    return (
        <span
            className="inline-flex h-[18px] shrink-0 items-center self-center rounded bg-[var(--mail-ext-bg)] px-1.5 text-[11px] font-semibold text-[var(--mail-ext-fg)]"
            title="The sender is outside your organisation"
        >
            External
        </span>
    );
}

/** "Today 08:47", "Yesterday 18:20", else "Tue, 15 Sept 2026, 07:24". */
export function formatMessageDate(iso: string): string {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    const time = d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    if (d >= today) return `Today ${time}`;
    if (d >= yesterday) return `Yesterday ${time}`;
    return d.toLocaleString(undefined, {
        weekday: "short", day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit"
    });
}

/** Collapsed-card date: "09:12" today, "Fri 12/09, 14:00" this year, "12/09/2025" before. */
function compactDate(iso: string): string {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    const now = new Date();
    const time = d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
    if (d.toDateString() === now.toDateString()) return time;
    if (d.getFullYear() === now.getFullYear()) {
        const day = d.toLocaleDateString(undefined, { weekday: "short" });
        const date = d.toLocaleDateString(undefined, { day: "2-digit", month: "2-digit" });
        return `${day} ${date}, ${time}`;
    }
    return d.toLocaleDateString(undefined, { day: "2-digit", month: "2-digit", year: "numeric" });
}

/**
 * Message header: 40 px avatar; sender, address and External tag over the To
 * line; the actions with the date under them on the right.
 */
export function MessageHead({
    from,
    isExternal,
    to,
    receivedAt,
    actions,
    onCollapse
}: {
    from: MailAddress;
    isExternal?: boolean;
    /** Omit while the full message is still loading. */
    to?: MailAddress[];
    receivedAt: string;
    actions?: ReactNode;
    /** Makes the sender line a "collapse this message" toggle (conversation view). */
    onCollapse?: () => void;
}) {
    return (
        <div className="grid grid-cols-[40px_minmax(0,1fr)_auto] items-start gap-3 px-4 pb-2.5 pt-3.5">
            <SenderAvatar address={from} className="h-10 w-10" textClassName="text-sm" />
            <div className="min-w-0">
                <SenderLine address={from} isExternal={isExternal} onCollapse={onCollapse} />
                {to ? <ToLine to={to} /> : null}
            </div>
            <div className="flex flex-col items-end gap-1">
                {actions ? <div className="flex flex-wrap items-center justify-end gap-0.5">{actions}</div> : null}
                <time
                    dateTime={receivedAt}
                    title={new Date(receivedAt).toLocaleString()}
                    className="whitespace-nowrap text-xs tabular-nums text-[var(--mail-text-2)]"
                >
                    {formatMessageDate(receivedAt)}
                </time>
            </div>
        </div>
    );
}

/** Sender name (bold), address and External tag, with a copy-address affordance. */
function SenderLine({
    address,
    isExternal,
    onCollapse
}: {
    address: MailAddress;
    isExternal?: boolean;
    onCollapse?: () => void;
}) {
    const announce = useStatusAnnouncer().announce;
    const [copied, setCopied] = useState(false);
    const name = address.name?.trim();

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(address.email);
            setCopied(true);
            announce(`Copied ${address.email}`);
            setTimeout(() => setCopied(false), 1500);
        } catch {
            // Clipboard unavailable (insecure context) — the text is still selectable.
        }
    };

    const label = (
        <>
            <span className="text-[14px] font-semibold text-foreground">{name || address.email}</span>
            {name ? <span className="break-all text-[13px] text-[var(--mail-text-2)]">&lt;{address.email}&gt;</span> : null}
        </>
    );

    return (
        <div className="group/from flex min-w-0 flex-wrap items-baseline gap-x-1.5 gap-y-0.5 leading-5">
            {onCollapse ? (
                <button
                    type="button"
                    onClick={onCollapse}
                    aria-expanded
                    aria-label={`Collapse message from ${name || address.email}`}
                    className="flex min-w-0 flex-wrap items-baseline gap-x-1.5 text-left focus:outline-none focus-visible:underline"
                >
                    {label}
                </button>
            ) : (
                <span className="flex min-w-0 select-text flex-wrap items-baseline gap-x-1.5" title={name ? `${name} <${address.email}>` : address.email}>
                    {label}
                </span>
            )}
            {isExternal ? <ExternalTag /> : null}
            <button
                type="button"
                onClick={() => void copy()}
                aria-label={`Copy ${address.email}`}
                title={copied ? "Copied" : "Copy address"}
                className="flex h-5 w-5 shrink-0 items-center justify-center self-center rounded text-muted-foreground opacity-0 transition-opacity hover:bg-[var(--mail-hover)] hover:text-foreground focus:opacity-100 group-hover/from:opacity-100"
            >
                {copied ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
            </button>
        </div>
    );
}

/** "To: Info; Hemant Kumar +2" — the first two, then the rest on request. */
function ToLine({ to }: { to: MailAddress[] }) {
    const [all, setAll] = useState(false);
    const shown = all ? to : to.slice(0, 2);
    const extra = to.length - shown.length;
    return (
        <div className="mt-0.5 flex min-w-0 flex-wrap items-center gap-x-0.5 text-[13px] text-[var(--mail-text-2)]">
            <span className="mr-1">To:</span>
            {to.length === 0 ? <span>Undisclosed recipients</span> : null}
            {shown.map((r, i) => (
                <span key={`${r.email}-${r.name}-${i}`} className="inline-flex items-center">
                    <RecipientChip address={r} variant="plain" />
                    {i < shown.length - 1 ? <span aria-hidden>;</span> : null}
                </span>
            ))}
            {extra > 0 ? (
                <button
                    type="button"
                    onClick={() => setAll(true)}
                    className="ml-1 rounded px-1 font-medium text-primary hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                    aria-label={`Show ${extra} more recipient${extra === 1 ? "" : "s"}`}
                >
                    +{extra}
                </button>
            ) : null}
        </div>
    );
}

/** A banner inside a message block — warning (amber) or neutral. */
export function NoticeBar({
    tone = "neutral",
    label,
    icon,
    actions,
    onDismiss,
    children
}: {
    tone?: "warn" | "neutral";
    label: string;
    icon: ReactNode;
    actions?: ReactNode;
    onDismiss?: () => void;
    children: ReactNode;
}) {
    return (
        <div
            role="note"
            aria-label={label}
            className={cn(
                "mx-4 mb-2.5 flex flex-wrap items-start gap-x-2 gap-y-1.5 rounded border px-2.5 py-2 text-[13px] leading-snug",
                tone === "warn"
                    ? "border-[var(--mail-warn-border)] bg-[var(--mail-warn-bg)] text-[var(--mail-warn-fg)]"
                    : "border-[var(--mail-line)] bg-[var(--mail-pane)] text-foreground/85"
            )}
        >
            <span className="mt-0.5 shrink-0" aria-hidden>{icon}</span>
            <div className="min-w-0 flex-1 basis-48">{children}</div>
            {actions ? <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div> : null}
            {onDismiss ? (
                <button
                    type="button"
                    onClick={onDismiss}
                    aria-label="Dismiss"
                    title="Dismiss"
                    className="shrink-0 rounded p-0.5 opacity-80 hover:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-current"
                >
                    <X className="h-4 w-4" />
                </button>
            ) : null}
        </div>
    );
}

/**
 * Warning for mail from outside the organisation (mail-bff
 * IExternalSenderClassifier decides; the rule lives server-side). Subtle on
 * purpose — phishing defence is DKIM/SPF/DMARC, not a banner.
 */
export function ExternalSenderBanner({ sender, onDismiss }: { sender: string; onDismiss: () => void }) {
    return (
        <NoticeBar tone="warn" label="External sender warning" icon={<ShieldAlert className="h-4 w-4" />} onDismiss={onDismiss}>
            This message came from <b className="font-semibold">{sender}</b>, outside your organisation — check links
            and attachments before you click, and never share passwords or one-time codes.
        </NoticeBar>
    );
}

/** The quoted-history toggle — the mock-up's "···" pill. */
export function QuoteToggle({ open, onToggle }: { open: boolean; onToggle: () => void }) {
    return (
        <button
            type="button"
            onClick={onToggle}
            aria-expanded={open}
            aria-label={open ? "Hide quoted text" : "Show quoted text"}
            title={open ? "Hide quoted text" : "Show quoted text"}
            className={cn(
                "mb-3 mt-1 inline-grid h-[18px] w-[30px] place-items-center rounded text-[11px] font-bold leading-none tracking-[1px] text-[var(--mail-text-2)] transition-colors",
                "hover:bg-[var(--mail-line-strong)] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary",
                open ? "bg-[var(--mail-line-strong)]" : "bg-[var(--mail-subtle)]"
            )}
        >
            ···
        </button>
    );
}

/** Reply · Reply all · Forward (· Reply with AI) at the end of a message. */
export function ReplyFooter({
    onReply,
    onReplyAll,
    onForward,
    onAiReply
}: {
    onReply?: () => void;
    onReplyAll?: () => void;
    onForward?: () => void;
    onAiReply?: () => void;
}) {
    if (!onReply && !onReplyAll && !onForward && !onAiReply) return null;
    const btn =
        "inline-flex h-8 items-center gap-2 rounded border border-[var(--mail-line-strong)] bg-card px-3.5 text-[13px] font-semibold text-foreground transition-colors hover:bg-[var(--mail-hover)] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary";
    return (
        <div className={cn("flex flex-wrap gap-2 pb-4 pt-1", COLUMN)}>
            {onReply ? (
                <button type="button" className={btn} onClick={onReply}>
                    <Reply className="h-4 w-4" aria-hidden />
                    Reply
                </button>
            ) : null}
            {onReplyAll ? (
                <button type="button" className={btn} onClick={onReplyAll}>
                    <ReplyAll className="h-4 w-4" aria-hidden />
                    Reply all
                </button>
            ) : null}
            {onForward ? (
                <button type="button" className={btn} onClick={onForward}>
                    <Forward className="h-4 w-4" aria-hidden />
                    Forward
                </button>
            ) : null}
            {onAiReply ? (
                <button type="button" className={cn(btn, "text-primary")} onClick={onAiReply} title="Draft a reply with AI">
                    <Sparkles className="h-4 w-4" aria-hidden />
                    Reply with AI
                </button>
            ) : null}
        </div>
    );
}

/** One-line card for a message that is not expanded: avatar, sender, snippet, date. */
export function CollapsedMessageCard({
    summary,
    onOpen,
    current,
    actionLabel = "Open message"
}: {
    summary: MessageSummary;
    onOpen?: () => void;
    /** The message the reader is showing (single-message thread list). */
    current?: boolean;
    actionLabel?: string;
}) {
    const who = summary.from.name || summary.from.email;
    const unread = !summary.isRead && !current;
    const inner = (
        <>
            <SenderAvatar address={summary.from} />
            <span className="min-w-0">
                <span className={cn("block truncate text-[13px] text-foreground", unread ? "font-bold" : "font-semibold")}>{who}</span>
                <span className="block truncate text-[13px] text-[var(--mail-text-2)]">{summary.preview || "(no preview)"}</span>
            </span>
            <span className="flex shrink-0 items-center gap-1.5">
                {unread ? <span className="h-2 w-2 rounded-full bg-primary" aria-hidden /> : null}
                {current ? (
                    <span className="rounded bg-[var(--mail-primary-soft)] px-1.5 py-0.5 text-[10px] font-semibold text-primary">Open</span>
                ) : null}
                <time dateTime={summary.receivedAt} className="text-xs tabular-nums text-[var(--mail-text-2)]">
                    {compactDate(summary.receivedAt)}
                </time>
            </span>
        </>
    );
    const card = "grid w-full grid-cols-[32px_minmax(0,1fr)_auto] items-center gap-2.5 rounded-lg border border-[var(--mail-line)] px-3.5 py-2.5 text-left";
    return (
        <div className="mx-5 mt-3">
            {onOpen ? (
                <button
                    type="button"
                    onClick={onOpen}
                    aria-expanded={false}
                    aria-label={`${who}, ${compactDate(summary.receivedAt)}${unread ? ", unread" : ""}. ${actionLabel}`}
                    className={cn(card, "bg-card transition-colors hover:bg-[var(--mail-hover)] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary")}
                >
                    {inner}
                </button>
            ) : (
                <div className={cn(card, current ? "bg-[var(--mail-primary-soft)]" : "bg-card")}>{inner}</div>
            )}
        </div>
    );
}

// ── Reading pane ─────────────────────────────────────────────────────────────

interface ReadingPaneProps {
    message: Message | null;
    isLoading?: boolean;
    /** The selected row: while its body loads the pane already shows its subject and sender. */
    preview?: MessageSummary | null;
    isError?: boolean;
    errorMessage?: string;
    onReply?: (message: Message) => void;
    onReplyAll?: (message: Message) => void;
    onForward?: (message: Message) => void;
    /** Per-message "AI reply" — opens a reply compose that auto-drafts with AI. */
    onAiReply?: (message: Message) => void;
    /** Open another message id in the same pane. Wired by the
     *  conversation panel so clicking a sibling row navigates to
     *  that message without leaving the reading pane. */
    onSelectMessage?: (messageId: string) => void;
}

/** Loading / error / nothing-selected: the same card, centred content. */
function ReaderState({ children, className }: { children: ReactNode; className?: string }) {
    return (
        <section className="flex h-full min-w-0 flex-1 flex-col overflow-hidden">
            <div className={cn("flex min-h-0 flex-1 items-center justify-center rounded-lg bg-card p-6 shadow-[var(--mail-shadow-4)]", className)}>
                {children}
            </div>
        </section>
    );
}

export function ReadingPane({
    message,
    isLoading,
    preview,
    isError,
    errorMessage,
    onReply,
    onReplyAll,
    onForward,
    onAiReply,
    onSelectMessage
}: ReadingPaneProps) {
    // Gmail-style auto-mark-read: once an unread message finishes loading
    // in the reading pane, flip it to read. `autoReadRef` guards against
    // firing twice for the same id — without it, a cache update from the
    // mutation would retrigger the effect when `message` re-renders with
    // isRead=true and then the user's actual manual "unread" click could
    // get clobbered.
    const autoRead = useMessageAction();
    const autoReadRef = useRef<string | null>(null);
    useEffect(() => {
        if (!message || isLoading) return;
        if (message.isRead) return;
        if (autoReadRef.current === message.id) return;
        autoReadRef.current = message.id;
        autoRead.mutate({ type: "read", id: message.id });
    }, [message, isLoading, autoRead]);

    if (isLoading && preview) {
        return <MessageLoading preview={preview} />;
    }

    if (isLoading) {
        return (
            <ReaderState className="text-muted-foreground">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
                <p className="text-sm">Loading message…</p>
            </ReaderState>
        );
    }

    if (isError) {
        return (
            <ReaderState className="text-destructive">
                <p className="text-sm">{errorMessage ?? "Could not load the selected message."}</p>
            </ReaderState>
        );
    }

    if (!message) {
        return (
            <ReaderState className="text-muted-foreground">
                <div className="flex flex-col items-center gap-4 text-center">
                    <EmptyEnvelopeArt />
                    <div>
                        <p className="text-sm font-semibold text-foreground">Select an item to read</p>
                        <p className="mt-0.5 text-sm">Nothing is selected</p>
                    </div>
                </div>
            </ReaderState>
        );
    }

    // Keyed by id so per-message state (summary, translation, expanded
    // quote, dismissed banners) resets when the selection changes.
    return (
        <MessageView
            key={message.id}
            message={message}
            onReply={onReply}
            onReplyAll={onReplyAll}
            onForward={onForward}
            onAiReply={onAiReply}
            onSelectMessage={onSelectMessage}
        />
    );
}

function MessageView({
    message,
    onReply,
    onReplyAll,
    onForward,
    onAiReply,
    onSelectMessage
}: {
    message: Message;
    onReply?: (m: Message) => void;
    onReplyAll?: (m: Message) => void;
    onForward?: (m: Message) => void;
    onAiReply?: (m: Message) => void;
    onSelectMessage?: (id: string) => void;
}) {
    const action = useMessageAction();
    const [showQuote, setShowQuote] = useState(false);
    const [unsubscribeConfirm, setUnsubscribeConfirm] = useState(false);
    const [externalDismissed, setExternalDismissed] = useState(false);

    // Quote folding: split the body where quoted-reply / forwarded history
    // begins, so the new message shows on its own and the history collapses
    // behind the "···" toggle. Every marker is a tag boundary, so bodyBefore
    // never ends mid-tag.
    const quoteIndex = findQuoteBoundary(message.bodyHtml);
    const hasQuote = quoteIndex !== -1;
    const bodyBefore = hasQuote ? message.bodyHtml.slice(0, quoteIndex) : message.bodyHtml;

    // ── Summarise ────────────────────────────────────────────────────────
    const summarizer = useAiChat();
    const [summaryOpen, setSummaryOpen] = useState(false);
    const [summary, setSummary] = useState<string | null>(null);
    const [summaryError, setSummaryError] = useState<string | null>(null);

    const summarize = async () => {
        setSummaryOpen(true);
        if (summary || summarizer.isPending) return;
        setSummaryError(null);
        const text = htmlToPlainText(bodyBefore).slice(0, 6000);
        if (!text.trim()) {
            setSummaryError("This message has no text to summarise.");
            return;
        }
        const from = message.from.name ? `${message.from.name} <${message.from.email}>` : message.from.email;
        try {
            // The instructions are repeated in the user turn so a BFF that
            // predates the "summarize" mode (it falls back to the general
            // prompt) still returns bullets.
            const resp = await summarizer.mutateAsync({
                mode: "summarize",
                messages: [{
                    role: "user",
                    content: `Summarise this email in 2-5 short bullet points, each line starting with "- ". No preamble.\n\nSubject: ${message.subject}\nFrom: ${from}\n\n${text}`
                }],
                emailContext: {
                    subject: message.subject,
                    fromName: message.from.name,
                    fromEmail: message.from.email,
                    bodyText: text.slice(0, 3000)
                }
            });
            setSummary(resp.reply.trim());
        } catch (e) {
            setSummaryError(e instanceof Error ? e.message : "Couldn't summarise this email.");
        }
    };

    // ── Translate ────────────────────────────────────────────────────────
    // Layout-preserving: every text piece of the email's HTML is translated
    // and written back in place, so the translated message looks exactly like
    // the original (images, tables, colours, links) — not a plain-text block.
    const segmentTranslator = useTranslateSegments();
    const [translation, setTranslation] = useState<{ lang: string; html: string; partial: boolean } | null>(null);
    const [translatingTo, setTranslatingTo] = useState<string | null>(null);
    const [translateError, setTranslateError] = useState<string | null>(null);
    const [showOriginal, setShowOriginal] = useState(false);

    const translate = async (lang: string) => {
        setTranslateError(null);
        setTranslatingTo(lang);
        try {
            // The whole body, quoted history included (it comes last, so a
            // long thread's budget runs out on the history, not the new text)
            // — keeps "show quoted text" working on the translation.
            const result = await translateEmailHtml(message.bodyHtml, async (segments) => {
                const resp = await segmentTranslator.mutateAsync({ segments, targetLang: lang });
                return { segments: resp.segments, partial: resp.partial };
            });
            setTranslation({ lang, html: result.html, partial: result.partial });
            setShowOriginal(false);
        } catch (e) {
            setTranslateError(e instanceof Error ? e.message : "Translation failed.");
        } finally {
            setTranslatingTo(null);
        }
    };

    const clearTranslation = () => {
        setTranslation(null);
        setTranslateError(null);
        setShowOriginal(false);
    };

    const showingTranslation = translation !== null && !showOriginal;
    const bodyToShow = showingTranslation
        ? (showQuote ? translation.html : sliceBeforeQuote(translation.html))
        : (showQuote ? message.bodyHtml : bodyBefore);

    // ── Safe senders ─────────────────────────────────────────────────────
    // Like Outlook: remote pictures (and tracking pixels) only load for your
    // own organisation and senders you have trusted.
    const auth = useAuth();
    const safeSenders = useSafeSenders();
    const addSafeSender = useAddSafeSender();
    const [showBlocked, setShowBlocked] = useState(false);
    const senderEmail = message.from.email.trim().toLowerCase();
    const senderDomain = senderEmail.split("@")[1] ?? "";
    const myDomain = (auth.user?.profile.email ?? "").trim().toLowerCase().split("@")[1] ?? "";
    const trusted =
        (senderDomain.length > 0 && senderDomain === myDomain) ||
        (safeSenders.data ?? []).some((s) => s.address === senderEmail || s.address === `@${senderDomain}`);
    const blockRemote = REMOTE_CONTENT.test(message.bodyHtml) && !trusted && !showBlocked;

    return (
        <ReaderShell label="Reading pane">
            <SubjectHeader
                subject={message.subject}
                chips={
                    <>
                        <CategoryChip category={message.category} />
                        <LabelChips messageId={message.id} />
                    </>
                }
                tools={
                    <>
                        <SummariseChip
                            active={summaryOpen}
                            pending={summarizer.isPending}
                            onClick={() => (summaryOpen && !summarizer.isPending ? setSummaryOpen(false) : void summarize())}
                        />
                        <TranslateMenu
                            current={translation?.lang ?? null}
                            pending={translatingTo !== null}
                            onPick={(lang) => void translate(lang)}
                            onShowOriginal={translation ? clearTranslation : undefined}
                        />
                        <IconButton
                            label="Open in new window"
                            onClick={() => window.open(`/mail/message/${encodeURIComponent(message.id)}`, "_blank", "noopener")}
                        >
                            <ExternalLink className="h-4 w-4" />
                        </IconButton>
                    </>
                }
            />

            {summaryOpen ? (
                <SummaryCard
                    pending={summarizer.isPending}
                    text={summary}
                    error={summaryError}
                    onClose={() => setSummaryOpen(false)}
                    onRetry={() => void summarize()}
                />
            ) : null}

            {translation || translatingTo || translateError ? (
                <div className="mx-5 mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 rounded-lg border border-[var(--mail-line)] bg-[var(--mail-pane)] px-3 py-1.5 text-[13px] text-[var(--mail-text-2)]">
                    <Languages className="h-4 w-4 shrink-0 text-primary" aria-hidden />
                    {translatingTo ? (
                        <span className="flex items-center gap-1.5">
                            Translating to {translatingTo}…
                            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                        </span>
                    ) : translateError ? (
                        <span className="text-destructive">{translateError}</span>
                    ) : translation ? (
                        <>
                            <span>
                                {showOriginal
                                    ? "Showing the original message."
                                    : `Translated to ${translation.lang} · machine translation.${translation.partial ? " Only the first part of this long message was translated." : ""}`}
                            </span>
                            <button
                                type="button"
                                onClick={() => setShowOriginal((v) => !v)}
                                className="font-medium text-primary hover:underline"
                            >
                                {showOriginal ? `Show ${translation.lang}` : "Show original"}
                            </button>
                        </>
                    ) : null}
                    <button
                        type="button"
                        onClick={clearTranslation}
                        aria-label="Close translation"
                        title="Close translation"
                        className="ml-auto rounded p-0.5 hover:bg-[var(--mail-hover)] hover:text-foreground"
                    >
                        <X className="h-3.5 w-3.5" />
                    </button>
                </div>
            ) : null}

            <article
                className="mx-5 mt-3 rounded-lg border border-[var(--mail-line)] bg-card"
                aria-label={`Message from ${message.from.name || message.from.email}`}
            >
                <MessageHead
                    from={message.from}
                    isExternal={message.isExternal}
                    to={message.to}
                    receivedAt={message.receivedAt}
                    actions={
                        <MessageActions
                            message={message}
                            onReply={onReply}
                            onReplyAll={onReplyAll}
                            onForward={onForward}
                            onAiReply={onAiReply}
                            onRequestUnsubscribe={() => setUnsubscribeConfirm(true)}
                        />
                    }
                />

                {unsubscribeConfirm ? (
                    <NoticeBar
                        label="Unsubscribe"
                        icon={<MailMinus className="h-4 w-4 text-muted-foreground" />}
                        actions={
                            <>
                                <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setUnsubscribeConfirm(false)}>
                                    Cancel
                                </Button>
                                <Button
                                    size="sm"
                                    variant="destructive"
                                    className="h-7 text-xs"
                                    onClick={() => {
                                        action.mutate({ type: "trash", id: message.id });
                                        setUnsubscribeConfirm(false);
                                    }}
                                >
                                    Unsubscribe
                                </Button>
                            </>
                        }
                    >
                        Unsubscribe and remove this message from your inbox?
                    </NoticeBar>
                ) : null}
                {message.isExternal && !externalDismissed ? (
                    <ExternalSenderBanner sender={message.from.email} onDismiss={() => setExternalDismissed(true)} />
                ) : null}
                {blockRemote ? (
                    <RemoteContentBanner
                        trusting={addSafeSender.isPending}
                        onTrust={() => addSafeSender.mutate(senderEmail)}
                        onShow={() => setShowBlocked(true)}
                    />
                ) : null}
                {message.receiptRequestedTo && !message.receiptResolved ? (
                    <ReceiptRequestBanner messageId={message.id} requestedTo={message.receiptRequestedTo} />
                ) : null}

                {message.attachments && message.attachments.length > 0 ? (
                    <AttachmentList messageId={message.id} attachments={message.attachments} />
                ) : null}

                <div className="max-w-[780px] pb-2 pl-4 pr-6 sm:pl-[68px]">
                    {/* Teams / Zoom / Meet / Webex / Zoho / GoTo … invite → Join card. */}
                    <MeetingJoinCard html={message.bodyHtml} className="mb-3" />
                    {/* Emails are authored for a white page — in dark mode the
                        body sits on its own white sheet, as Outlook does with
                        "light background" on. The translation renders in the
                        same frame, so it keeps the original layout. */}
                    <div className="overflow-hidden rounded-md dark:bg-white dark:p-4">
                        <EmailBodyFrame html={bodyToShow} blockRemote={blockRemote} />
                    </div>
                    {hasQuote ? <QuoteToggle open={showQuote} onToggle={() => setShowQuote((v) => !v)} /> : null}
                    {/* Drive links in the body resolve to cards here, in
                        React — the sandboxed body frame can't fetch Drive. */}
                    <DriveLinkCards html={message.bodyHtml} />
                </div>

                <ReplyFooter
                    onReply={onReply ? () => onReply(message) : undefined}
                    onReplyAll={onReplyAll ? () => onReplyAll(message) : undefined}
                    onForward={onForward ? () => onForward(message) : undefined}
                    onAiReply={onAiReply ? () => onAiReply(message) : undefined}
                />
            </article>

            <ConversationPanel currentMessageId={message.id} onSelectMessage={onSelectMessage} />
        </ReaderShell>
    );
}

/**
 * The pane while a message loads: the same layout as the message, with the subject and sender from the list row and a
 * pulsing body, so switching messages (j/k, delete, archive) doesn't flash an empty pane.
 */
function MessageLoading({ preview }: { preview: MessageSummary }) {
    return (
        <ReaderShell label="Reading pane">
            <div aria-busy="true" aria-live="polite">
                <SubjectHeader
                    subject={preview.subject || "(no subject)"}
                    chips={<CategoryChip category={preview.category} />}
                    tools={<Loader2 className="h-4 w-4 animate-spin text-[var(--mail-text-2)]" aria-label="Loading message" />}
                />
                <article className="mx-5 mt-3 rounded-lg border border-[var(--mail-line)] bg-card">
                    <MessageHead from={preview.from} isExternal={preview.isExternal} receivedAt={preview.receivedAt} />
                    <div className="max-w-[780px] space-y-2.5 pb-6 pl-4 pr-6 pt-2 sm:pl-[68px]">
                        {preview.preview ? (
                            <p className="line-clamp-2 text-[15px] leading-[1.45] text-[var(--mail-text-2)]">{preview.preview}</p>
                        ) : null}
                        <div className="h-3 w-11/12 animate-pulse rounded bg-[var(--mail-subtle)]" />
                        <div className="h-3 w-4/5 animate-pulse rounded bg-[var(--mail-subtle)]" />
                        <div className="h-3 w-2/3 animate-pulse rounded bg-[var(--mail-subtle)]" />
                        <div className="h-3 w-3/4 animate-pulse rounded bg-[var(--mail-subtle)]" />
                    </div>
                </article>
            </div>
        </ReaderShell>
    );
}

/** Outlook-style "nothing selected" illustration: an open envelope with a letter. */
function EmptyEnvelopeArt() {
    return (
        <svg width="136" height="120" viewBox="0 0 136 120" aria-hidden className="drop-shadow-sm">
            <defs>
                <linearGradient id="rp-env-flap" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0" stopColor="#a78bfa" />
                    <stop offset="0.55" stopColor="#8b5cf6" />
                    <stop offset="1" stopColor="#3b82f6" />
                </linearGradient>
                <linearGradient id="rp-env-letter" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0" stopColor="#ffffff" />
                    <stop offset="1" stopColor="#e0f2fe" />
                </linearGradient>
                <linearGradient id="rp-env-body" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0" stopColor="#f8fafc" />
                    <stop offset="1" stopColor="#dbe4ee" />
                </linearGradient>
                <linearGradient id="rp-env-side" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0" stopColor="#60a5fa" />
                    <stop offset="1" stopColor="#2563eb" />
                </linearGradient>
            </defs>
            {/* open flap behind the letter */}
            <path d="M12 52 L68 10 L124 52 Z" fill="url(#rp-env-flap)" />
            {/* letter */}
            <rect x="32" y="24" width="72" height="62" rx="5" fill="url(#rp-env-letter)" stroke="#e2e8f0" />
            <rect x="42" y="36" width="40" height="4" rx="2" fill="#cbd5e1" />
            <rect x="42" y="46" width="52" height="4" rx="2" fill="#e2e8f0" />
            {/* side panels */}
            <path d="M12 52 L56 84 L12 110 Z" fill="url(#rp-env-side)" opacity="0.9" />
            <path d="M124 52 L80 84 L124 110 Z" fill="url(#rp-env-side)" opacity="0.75" />
            {/* front pocket */}
            <path d="M12 110 L68 70 L124 110 V112 a6 6 0 0 1 -6 6 H18 a6 6 0 0 1 -6 -6 Z" fill="url(#rp-env-body)" />
        </svg>
    );
}

/** The mock-up's "Summarise" chip in the subject header. */
function SummariseChip({ active, pending, onClick }: { active: boolean; pending: boolean; onClick: () => void }) {
    return (
        <button
            type="button"
            onClick={onClick}
            aria-pressed={active}
            title="Summarise this email"
            className={cn(
                "inline-flex h-[30px] items-center gap-1.5 rounded bg-[var(--mail-primary-soft)] px-2.5 text-[13px] font-semibold text-primary transition-[filter]",
                "hover:brightness-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary",
                active && "ring-1 ring-inset ring-primary/40"
            )}
        >
            {pending ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <Sparkles className="h-4 w-4" aria-hidden />}
            Summarise
        </button>
    );
}

const TRANSLATE_LANGS = [
    "English", "Spanish", "French", "German", "Hindi", "Arabic",
    "Chinese (Simplified)", "Japanese", "Portuguese", "Russian", "Italian", "Dutch"
];

/**
 * On-demand translation, tucked behind one icon. Uses the free
 * /api/translate endpoint (no Claude/Anthropic key needed) so it works on
 * every deployment; the result renders inline with a toggle to the original.
 */
function TranslateMenu({
    current,
    pending,
    onPick,
    onShowOriginal
}: {
    current: string | null;
    pending: boolean;
    onPick: (lang: string) => void;
    onShowOriginal?: () => void;
}) {
    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button
                    variant="ghost"
                    size="icon"
                    className={cn(ICON_BTN, current && "bg-[var(--mail-primary-soft)] text-primary")}
                    aria-label="Translate"
                    title="Translate"
                >
                    {pending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Languages className="h-4 w-4" />}
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="max-h-80 w-52 overflow-y-auto">
                <DropdownMenuLabel className="text-xs font-semibold text-muted-foreground">Translate to</DropdownMenuLabel>
                {TRANSLATE_LANGS.map((l) => (
                    <DropdownMenuItem key={l} onSelect={() => onPick(l)}>
                        <span className="flex-1">{l}</span>
                        {current === l ? <Check className="h-4 w-4 text-primary" /> : null}
                    </DropdownMenuItem>
                ))}
                {onShowOriginal ? (
                    <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onSelect={onShowOriginal}>Show original</DropdownMenuItem>
                    </>
                ) : null}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}

/** The AI summary, under the subject header. */
function SummaryCard({
    pending,
    text,
    error,
    onClose,
    onRetry
}: {
    pending: boolean;
    text: string | null;
    error: string | null;
    onClose: () => void;
    onRetry: () => void;
}) {
    const bullets = (text ?? "")
        .split(/\r?\n/)
        .map((l) => l.trim())
        .filter(Boolean)
        .map((l) => l.replace(/^([-*•]|\d+[.)])\s*/, "").replace(/\*\*(.+?)\*\*/g, "$1"));

    return (
        <div
            role="region"
            aria-label="Email summary"
            className="mx-5 mt-3 rounded-lg border border-[var(--mail-line)] bg-[var(--mail-pane)] px-3.5 py-3"
        >
            <div className="flex items-center gap-1.5">
                <Sparkles className="h-4 w-4 text-primary" aria-hidden />
                <h3 className="text-[13px] font-semibold text-foreground">Summary</h3>
                <button
                    type="button"
                    onClick={onClose}
                    aria-label="Close summary"
                    title="Close summary"
                    className="ml-auto rounded p-0.5 text-muted-foreground hover:bg-[var(--mail-hover)] hover:text-foreground"
                >
                    <X className="h-4 w-4" />
                </button>
            </div>
            {pending ? (
                <div className="mt-2.5 space-y-2" aria-label="Summarising">
                    <div className="h-3 w-11/12 animate-pulse rounded bg-[var(--mail-subtle)]" />
                    <div className="h-3 w-4/5 animate-pulse rounded bg-[var(--mail-subtle)]" />
                    <div className="h-3 w-2/3 animate-pulse rounded bg-[var(--mail-subtle)]" />
                </div>
            ) : error ? (
                <p className="mt-2 text-[13px] text-destructive">
                    {error}{" "}
                    <button type="button" onClick={onRetry} className="font-medium underline underline-offset-2">
                        Try again
                    </button>
                </p>
            ) : (
                <>
                    <ul className="mt-1.5 space-y-1 text-[13px] leading-snug text-foreground">
                        {bullets.map((b, i) => (
                            <li key={i} className="flex gap-2">
                                <span className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-primary" aria-hidden />
                                <span>{b}</span>
                            </li>
                        ))}
                    </ul>
                    <p className="mt-2 text-[11px] text-[var(--mail-text-3)]">AI-generated content may be incorrect.</p>
                </>
            )}
        </div>
    );
}

/**
 * The other messages of this message's thread (sorted oldest→newest), as
 * one-line cards under the message. The open one is marked; the others open
 * in this pane. Hidden when the thread has only this message.
 */
function ConversationPanel({
    currentMessageId,
    onSelectMessage
}: {
    currentMessageId: string;
    onSelectMessage?: (id: string) => void;
}) {
    const { data, isLoading } = useThreadByMessage(currentMessageId);
    const siblings = (data ?? []) as MessageSummary[];

    if (isLoading) return null;
    if (siblings.length <= 1) return null;

    const sorted = [...siblings].sort(
        (a, b) => new Date(a.receivedAt).getTime() - new Date(b.receivedAt).getTime()
    );

    return (
        <section className="mt-6" aria-label="Conversation thread">
            <p className="mx-5 text-xs font-semibold text-[var(--mail-text-2)]">{sorted.length} messages in this conversation</p>
            {sorted.map((s) => {
                const isCurrent = s.id === currentMessageId;
                return (
                    <CollapsedMessageCard
                        key={s.id}
                        summary={s}
                        current={isCurrent}
                        onOpen={isCurrent || !onSelectMessage ? undefined : () => onSelectMessage(s.id)}
                    />
                );
            })}
        </section>
    );
}

/** Trigger a print-friendly window for the given message */
function printMessage(msg: Message): void {
    const receivedDate = new Date(msg.receivedAt).toLocaleString(undefined, {
        dateStyle: "medium",
        timeStyle: "short"
    });
    const toList = msg.to.map((r) => (r.name ? `${r.name} <${r.email}>` : r.email)).join(", ");
    const html = `<!DOCTYPE html>
<html><head><meta charset="utf-8"/>
<title>${escapeHtml(msg.subject)}</title>
<style>
  body { font-family: sans-serif; margin: 32px; color: #111; }
  .header { border-bottom: 1px solid #ccc; margin-bottom: 16px; padding-bottom: 16px; }
  .header dt { font-weight: bold; display: inline; }
  .header dd { display: inline; margin-left: 4px; }
  .meta { color: #555; font-size: .875rem; }
  article img { max-width: 100%; }
</style>
</head><body>
<div class="header">
  <h1>${escapeHtml(msg.subject)}</h1>
  <div class="meta">
    <dl><dt>From:</dt><dd>${escapeHtml(msg.from.name ? `${msg.from.name} <${msg.from.email}>` : msg.from.email)}</dd></dl>
    <dl><dt>To:</dt><dd>${escapeHtml(toList)}</dd></dl>
    <dl><dt>Date:</dt><dd>${escapeHtml(receivedDate)}</dd></dl>
  </div>
</div>
<article>${msg.bodyHtml}</article>
</body></html>`;
    const w = window.open("", "_blank", "width=800,height=600");
    if (!w) return;
    w.document.write(html);
    w.document.close();
    w.focus();
    w.print();
}

function escapeHtml(s: string): string {
    return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}

/** Compose a minimal RFC 5322 .eml and trigger a browser download */
function downloadEml(msg: Message): void {
    const receivedDate = new Date(msg.receivedAt).toUTCString();
    const toList = msg.to.map((r) => (r.name ? `${r.name} <${r.email}>` : r.email)).join(", ");
    const fromLine = msg.from.name ? `${msg.from.name} <${msg.from.email}>` : msg.from.email;
    const eml = [
        `From: ${fromLine}`,
        `To: ${toList}`,
        `Subject: ${msg.subject}`,
        `Date: ${receivedDate}`,
        `MIME-Version: 1.0`,
        `Content-Type: text/html; charset=utf-8`,
        ``,
        msg.bodyHtml
    ].join("\r\n");
    const blob = new Blob([eml], { type: "message/rfc822" });
    const url = URL.createObjectURL(blob);
    try {
        const a = document.createElement("a");
        a.href = url;
        const safeSubject = msg.subject.replace(/[^a-z0-9_\- ]/gi, "_").slice(0, 60);
        a.download = `${safeSubject}.eml`;
        document.body.appendChild(a);
        a.click();
        a.remove();
    } finally {
        URL.revokeObjectURL(url);
    }
}

/** Whether to show an unsubscribe button (heuristic — no headers available yet) */
function looksLikeNewsletter(msg: Message): boolean {
    const haystack = `${msg.subject} ${msg.preview}`.toLowerCase();
    return msg.category === "social" || haystack.includes("unsubscribe");
}

function htmlToPlainText(html: string): string {
    const el = document.createElement("div");
    el.innerHTML = html;
    return (el.textContent ?? el.innerText ?? "").replace(/\n{3,}/g, "\n\n").trim();
}

/**
 * The message header's actions, as in the mock-up: Reply · Reply all ·
 * Forward, then Labels and Snooze (their own popovers), then "More actions"
 * with everything else — triage, pin, tasks, print, EML, unsubscribe, spam —
 * so the pane is complete even without the command bar.
 */
function MessageActions({
    message,
    onReply,
    onReplyAll,
    onForward,
    onAiReply,
    onRequestUnsubscribe
}: {
    message: Message;
    onReply?: (m: Message) => void;
    onReplyAll?: (m: Message) => void;
    onForward?: (m: Message) => void;
    onAiReply?: (m: Message) => void;
    onRequestUnsubscribe: () => void;
}) {
    const action = useMessageAction();
    const taskTargets = useTaskTargets();
    const createTaskFrom = useCreateTaskFromMessage();
    const run = (args: Parameters<typeof action.mutate>[0]) => action.mutate(args);
    const isNewsletter = looksLikeNewsletter(message);

    return (
        <>
            {onReply ? (
                <IconButton label="Reply" onClick={() => onReply(message)}>
                    <Reply className="h-4 w-4" />
                </IconButton>
            ) : null}
            {onReplyAll ? (
                <IconButton label="Reply all" onClick={() => onReplyAll(message)}>
                    <ReplyAll className="h-4 w-4" />
                </IconButton>
            ) : null}
            {onForward ? (
                <IconButton label="Forward" onClick={() => onForward(message)}>
                    <Forward className="h-4 w-4" />
                </IconButton>
            ) : null}
            <LabelPicker messageId={message.id} triggerClassName={ICON_BTN} />
            <SnoozeMenu messageId={message.id} triggerClassName={ICON_BTN} />
            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className={ICON_BTN} aria-label="More actions" title="More actions">
                        <MoreHorizontal className="h-4 w-4" />
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                    {onAiReply ? (
                        <>
                            <DropdownMenuItem onSelect={() => onAiReply(message)}>
                                <Sparkles className="h-4 w-4" />
                                Reply with AI
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                        </>
                    ) : null}
                    <DropdownMenuItem onSelect={() => run({ type: message.isFlagged ? "unflag" : "flag", id: message.id })}>
                        <Flag className={cn("h-4 w-4", message.isFlagged && "fill-red-500 text-red-500")} />
                        {message.isFlagged ? "Unflag" : "Flag"}
                    </DropdownMenuItem>
                    <DropdownMenuItem onSelect={() => run({ type: message.isRead ? "unread" : "read", id: message.id })}>
                        {message.isRead ? <Mail className="h-4 w-4" /> : <MailOpen className="h-4 w-4" />}
                        {message.isRead ? "Mark as unread" : "Mark as read"}
                    </DropdownMenuItem>
                    <DropdownMenuItem onSelect={() => run({ type: message.isPinned ? "unpin" : "pin", id: message.id })}>
                        {message.isPinned ? <PinOff className="h-4 w-4" /> : <Pin className="h-4 w-4" />}
                        {message.isPinned ? "Unpin from top" : "Pin to top of folder"}
                    </DropdownMenuItem>
                    <DropdownMenuItem onSelect={() => run({ type: "archive", id: message.id })}>
                        <Archive className="h-4 w-4" />
                        Archive
                    </DropdownMenuItem>
                    <DropdownMenuItem
                        className="text-destructive focus:text-destructive"
                        onSelect={() => run({ type: "trash", id: message.id })}
                    >
                        <Trash2 className="h-4 w-4" />
                        Delete
                    </DropdownMenuItem>
                    <DropdownMenuSub>
                        <DropdownMenuSubTrigger>
                            <ListTodo className="h-4 w-4" />
                            Create task
                        </DropdownMenuSubTrigger>
                        <DropdownMenuSubContent className="max-h-72 w-48 overflow-y-auto">
                            {taskTargets.map((t) => (
                                <DropdownMenuItem
                                    key={t.key}
                                    onSelect={() => createTaskFrom(
                                        [{ id: message.id, subject: message.subject, fromName: message.from.name, fromEmail: message.from.email }],
                                        t.key
                                    )}
                                >
                                    {t.emoji ? <span aria-hidden>{t.emoji}</span> : null}
                                    {t.label}
                                </DropdownMenuItem>
                            ))}
                        </DropdownMenuSubContent>
                    </DropdownMenuSub>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onSelect={() => printMessage(message)}>
                        <Printer className="h-4 w-4" />
                        Print
                    </DropdownMenuItem>
                    <DropdownMenuItem onSelect={() => downloadEml(message)}>
                        <Download className="h-4 w-4" />
                        Download as EML
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    {isNewsletter ? (
                        <DropdownMenuItem onSelect={onRequestUnsubscribe}>
                            <MailMinus className="h-4 w-4" />
                            Unsubscribe…
                        </DropdownMenuItem>
                    ) : null}
                    <DropdownMenuItem onSelect={() => run({ type: "move", id: message.id, folderId: "junk" })}>
                        <ShieldAlert className="h-4 w-4" />
                        Report spam
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>
        </>
    );
}

/** Coloured file-type tile (the mock-up's attachment card). */
function fileTileOf(fileName: string, contentType: string): { label: string; color: string } {
    const ext = (fileName.includes(".") ? fileName.split(".").pop() ?? "" : "").toLowerCase();
    if (ext === "pdf" || contentType === "application/pdf") return { label: "PDF", color: "#c42b1c" };
    if (["xls", "xlsx", "xlsm", "csv", "ods"].includes(ext)) return { label: "XLS", color: "#107c41" };
    if (["doc", "docx", "odt", "rtf"].includes(ext)) return { label: "DOC", color: "#185abd" };
    if (["ppt", "pptx", "odp"].includes(ext)) return { label: "PPT", color: "#c43e1c" };
    if (contentType.startsWith("image/") || ["png", "jpg", "jpeg", "gif", "webp", "svg", "heic", "bmp"].includes(ext)) {
        return { label: "IMG", color: "#8764b8" };
    }
    if (["zip", "rar", "7z", "gz", "tar"].includes(ext)) return { label: "ZIP", color: "#7a5c1e" };
    return { label: ext ? ext.slice(0, 4).toUpperCase() : "FILE", color: "#5b6875" };
}

function FileTile({ fileName, contentType }: { fileName: string; contentType: string }) {
    const { label, color } = fileTileOf(fileName, contentType);
    return (
        <span
            aria-hidden
            className="grid h-8 w-7 place-items-center rounded-[3px] text-[9px] font-bold tracking-wide text-white"
            style={{ backgroundColor: color }}
        >
            {label}
        </span>
    );
}

export function AttachmentList({
    messageId,
    attachments
}: {
    messageId: string;
    attachments: MessageAttachment[];
}) {
    const token = useAccessToken();
    const [downloading, setDownloading] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [previewing, setPreviewing] = useState<MessageAttachment | null>(null);

    // "Save to Drive" / "Save all to Drive" — the user picks a folder, then
    // each attachment's bytes come from the Mail BFF and go straight to the
    // Drive BFF (upload progress per card). A toast offers "Open in Drive".
    const [saveTargets, setSaveTargets] = useState<MessageAttachment[] | null>(null);
    const [driveProgress, setDriveProgress] = useState<Record<string, number>>({});
    const [savedToast, setSavedToast] = useState<{ text: string; href: string } | null>(null);
    const { announce } = useStatusAnnouncer();

    const downloadable = attachments.filter((a) => !a.isInline);
    if (downloadable.length === 0) return null;

    const handleDownload = async (a: MessageAttachment) => {
        setError(null);
        setDownloading(a.id);
        try {
            await mailApi.downloadInboundAttachment(messageId, a.id, a.fileName, token);
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
        } finally {
            setDownloading(null);
        }
    };

    // One after another (no zip): the browser may ask once to allow several downloads.
    const handleDownloadAll = async () => {
        setError(null);
        for (const a of downloadable) {
            setDownloading(a.id);
            try {
                await mailApi.downloadInboundAttachment(messageId, a.id, a.fileName, token);
            } catch (err) {
                setError(`${a.fileName}: ${err instanceof Error ? err.message : String(err)}`);
                break;
            }
        }
        setDownloading(null);
        announce("Downloaded all attachments");
    };

    // Every office/PDF attachment previews inline via the dialog — no new
    // tab, no auth round-trip. The dialog picks the viewer by extension (PDF
    // direct; docx/xlsx/pptx route through the Office BFFs' /ToPdf).
    // PDF, Office, images and text all open in the quick view (quickView.ts).
    const isPreviewable = (a: MessageAttachment) => {
        if (canQuickView(a.fileName, a.contentType)) return true;
        const t = resolveOpenInTarget(a.contentType, a.fileName);
        return Boolean(t && !t.comingSoon && (t.appId === "pdfViewer" || t.appId === "docs" || t.appId === "sheet" || t.appId === "present"));
    };

    const openInSuite = (a: MessageAttachment) => {
        // Previewable first: images and text have no suite app to open in.
        if (isPreviewable(a)) {
            setPreviewing(a);
            return;
        }
        const target = resolveOpenInTarget(a.contentType, a.fileName);
        if (!target || target.comingSoon) return;
        const downloadUrl = baseUrl
            ? new URL(
                  `/api/messages/${encodeURIComponent(messageId)}/attachments/${encodeURIComponent(a.id)}`,
                  baseUrl
              ).toString()
            : "";
        const openUrl = buildOpenInUrl(target, downloadUrl, a.fileName);
        window.open(openUrl, "_blank", "noopener,noreferrer");
    };

    const saveToDrive = async (list: MessageAttachment[], folder: { id: string | null; name: string }) => {
        setError(null);
        const saved: DriveItem[] = [];
        const failures: string[] = [];
        setDriveProgress((p) => Object.fromEntries([...Object.entries(p), ...list.map((a) => [a.id, 0] as const)]));
        for (const a of list) {
            try {
                if (!baseUrl) throw new Error("Mail BFF not configured");
                const url = new URL(
                    `/api/messages/${encodeURIComponent(messageId)}/attachments/${encodeURIComponent(a.id)}`,
                    baseUrl
                ).toString();
                const res = await fetch(url, {
                    headers: token ? { Authorization: `Bearer ${token}` } : {}
                });
                if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
                const blob = await res.blob();
                const file = new File([blob], a.fileName, { type: a.contentType });
                const item = await driveApi.uploadFile(file, folder.id, token, (fraction) =>
                    setDriveProgress((p) => ({ ...p, [a.id]: fraction })));
                saved.push(item);
            } catch (err) {
                failures.push(`${a.fileName}: ${err instanceof Error ? err.message : String(err)}`);
            } finally {
                setDriveProgress((p) => {
                    const next = { ...p };
                    delete next[a.id];
                    return next;
                });
            }
        }
        if (failures.length > 0) setError(`Save to Drive failed — ${failures.join("; ")}`);
        if (saved.length === 1) {
            const only = saved[0]!;
            setSavedToast({ text: `Saved ${only.name} to ${folder.name}`, href: driveOpenUrl(only.id) });
            announce(`Saved ${only.name} to Drive`);
        } else if (saved.length > 1) {
            setSavedToast({ text: `Saved ${saved.length} files to ${folder.name}`, href: driveFolderUrl(folder.id) });
            announce(`Saved ${saved.length} files to Drive`);
        }
    };

    const previewDownloadUrl = previewing && baseUrl
        ? new URL(
              `/api/messages/${encodeURIComponent(messageId)}/attachments/${encodeURIComponent(previewing.id)}`,
              baseUrl
          ).toString()
        : "";

    // Gallery: the subset of attachments that open in the inline viewer, so
    // prev/next can page through them without closing the dialog.
    const previewable = downloadable.filter(isPreviewable);
    const previewIndex = previewing ? previewable.findIndex((a) => a.id === previewing.id) : -1;
    const stepPreview = (delta: number) => {
        if (previewIndex < 0 || previewable.length === 0) return;
        const next = (previewIndex + delta + previewable.length) % previewable.length;
        setPreviewing(previewable[next] ?? null);
    };

    const totalBytes = downloadable.reduce((sum, a) => sum + a.sizeBytes, 0);
    const savingAny = Object.keys(driveProgress).length > 0;
    const rowBtn =
        "inline-flex items-center gap-1.5 rounded transition-colors hover:text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-50 disabled:hover:text-inherit";

    return (
        <div>
            {/* Outlook-style attachment cards: click the card to open it, the
                chevron for Save to Drive / Download. */}
            <div className={cn("flex flex-wrap gap-2 pb-2", COLUMN)}>
                {downloadable.map((a) => {
                    const target = resolveOpenInTarget(a.contentType, a.fileName);
                    const canOpen = Boolean(target && !target.comingSoon) || canQuickView(a.fileName, a.contentType);
                    const saving = driveProgress[a.id];
                    const isDownloading = downloading === a.id;
                    return (
                        <div
                            key={a.id}
                            className="grid h-[52px] w-[232px] max-w-full grid-cols-[minmax(0,1fr)_28px] items-center gap-1 rounded border border-[var(--mail-line-strong)] bg-card pl-2.5 pr-1 transition-colors hover:bg-[var(--mail-hover)]"
                        >
                            <button
                                type="button"
                                className="grid min-w-0 grid-cols-[32px_minmax(0,1fr)] items-center gap-2 rounded text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                                onClick={() => (canOpen ? openInSuite(a) : void handleDownload(a))}
                                title={canOpen && target ? `${a.fileName} — ${isPreviewable(a) ? "Preview" : `Open in ${target.appName}`}` : `${a.fileName} — Download`}
                            >
                                <FileTile fileName={a.fileName} contentType={a.contentType} />
                                <span className="min-w-0">
                                    <span className="block truncate text-[13px] text-foreground">{a.fileName}</span>
                                    {saving !== undefined ? (
                                        <span
                                            className="block text-[11px] tabular-nums text-[var(--mail-text-3)]"
                                            role="progressbar"
                                            aria-label={`Saving ${a.fileName} to Drive`}
                                            aria-valuemin={0}
                                            aria-valuemax={100}
                                            aria-valuenow={Math.floor(saving * 100)}
                                        >
                                            Saving to Drive · {Math.floor(saving * 100)}%
                                        </span>
                                    ) : isDownloading ? (
                                        <span className="flex items-center gap-1 text-[11px] text-[var(--mail-text-3)]">
                                            <Loader2 className="h-3 w-3 animate-spin" aria-hidden /> Downloading…
                                        </span>
                                    ) : (
                                        <span className="block text-[11px] tabular-nums text-[var(--mail-text-3)]">{formatBytes(a.sizeBytes)}</span>
                                    )}
                                </span>
                            </button>
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-7 w-7 text-[var(--mail-text-2)] hover:bg-[var(--mail-subtle)]"
                                        aria-label={`Actions for ${a.fileName}`}
                                    >
                                        <ChevronDown className="h-4 w-4" />
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-52">
                                    {target ? (
                                        <DropdownMenuItem disabled={target.comingSoon} onSelect={() => openInSuite(a)}>
                                            <ExternalLink className="h-4 w-4" />
                                            {target.comingSoon ? `${target.appName} · coming soon` : isPreviewable(a) ? "Preview" : `Open in ${target.appName}`}
                                        </DropdownMenuItem>
                                    ) : null}
                                    {driveEnabled ? (
                                        <DropdownMenuItem disabled={saving !== undefined} onSelect={() => setSaveTargets([a])}>
                                            <HardDrive className="h-4 w-4" />
                                            Save to Drive…
                                        </DropdownMenuItem>
                                    ) : null}
                                    <DropdownMenuItem disabled={downloading === a.id} onSelect={() => void handleDownload(a)}>
                                        <Download className="h-4 w-4" />
                                        Download
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        </div>
                    );
                })}
            </div>
            <div className={cn("flex flex-wrap items-center gap-x-4 gap-y-1 pb-3 text-xs text-[var(--mail-text-2)]", COLUMN)}>
                <span className="inline-flex items-center gap-1.5">
                    <Paperclip className="h-3.5 w-3.5" aria-hidden />
                    {downloadable.length} attachment{downloadable.length === 1 ? "" : "s"} ({formatBytes(totalBytes)})
                </span>
                {driveEnabled && downloadable.length > 1 ? (
                    <button type="button" onClick={() => setSaveTargets(downloadable)} disabled={savingAny} className={rowBtn}>
                        <CloudUpload className="h-3.5 w-3.5" aria-hidden />
                        Save all to Drive
                    </button>
                ) : null}
                {downloadable.length > 1 ? (
                    <button type="button" onClick={() => void handleDownloadAll()} disabled={downloading !== null} className={rowBtn}>
                        <Download className="h-3.5 w-3.5" aria-hidden />
                        Download all
                    </button>
                ) : null}
            </div>
            {error ? <p className={cn("-mt-1.5 pb-2 text-xs text-destructive", COLUMN)}>{error}</p> : null}
            {driveEnabled ? (
                <DriveFilePicker
                    mode="folder"
                    open={saveTargets !== null}
                    onOpenChange={(o) => {
                        if (!o) setSaveTargets(null);
                    }}
                    title={saveTargets && saveTargets.length > 1 ? `Save ${saveTargets.length} attachments to Drive` : "Save to Drive"}
                    description={saveTargets && saveTargets.length === 1
                        ? `Choose where to save ${saveTargets[0]?.fileName ?? "the attachment"}.`
                        : "Choose a folder in My Drive."}
                    onPickFolder={(folder) => {
                        const list = saveTargets ?? [];
                        setSaveTargets(null);
                        void saveToDrive(list, folder);
                    }}
                />
            ) : null}
            {savedToast ? (
                <DriveSavedToast text={savedToast.text} href={savedToast.href} onDismiss={() => setSavedToast(null)} />
            ) : null}
            <PdfPreviewDialog
                open={previewing !== null}
                fileName={previewing?.fileName ?? ""}
                downloadUrl={previewDownloadUrl}
                accessToken={token}
                onClose={() => setPreviewing(null)}
                onPrev={() => stepPreview(-1)}
                onNext={() => stepPreview(1)}
                position={previewIndex >= 0 ? { index: previewIndex + 1, total: previewable.length } : undefined}
            />
        </div>
    );
}

function formatBytes(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

/** Bottom-left confirmation after Save to Drive, with an "Open in Drive" link. */
function DriveSavedToast({ text, href, onDismiss }: { text: string; href: string; onDismiss: () => void }) {
    useEffect(() => {
        const id = setTimeout(onDismiss, 8000);
        return () => clearTimeout(id);
    }, [onDismiss]);
    return (
        <div
            role="status"
            aria-live="polite"
            className="pointer-events-auto fixed bottom-6 left-6 z-50 flex max-w-[calc(100vw-3rem)] items-center gap-3 rounded-md bg-[#0078D4] px-4 py-3 text-sm text-white shadow-lg sm:max-w-sm"
        >
            <CheckCircle2 className="h-4 w-4 shrink-0" aria-hidden />
            <span className="min-w-0 flex-1 truncate" title={text}>{text}</span>
            <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="shrink-0 font-semibold underline-offset-2 hover:underline focus:outline-none focus-visible:underline"
            >
                Open in Drive
            </a>
            <button
                type="button"
                onClick={onDismiss}
                aria-label="Dismiss"
                className="shrink-0 opacity-70 hover:opacity-100 focus:outline-none focus-visible:opacity-100"
            >
                <X className="h-4 w-4" />
            </button>
        </div>
    );
}

/**
 * Outlook-style "Sender requested a read receipt" prompt. Two
 * actions: send the MDN (POST /api/messages/{id}/send-receipt) or
 * dismiss without sending (dismiss-receipt). Both flip
 * receiptResolved=true so the banner doesn't reappear next visit.
 */
function ReceiptRequestBanner({
    messageId,
    requestedTo
}: {
    messageId: string;
    requestedTo: string;
}) {
    const send = useSendReadReceipt();
    const dismiss = useDismissReadReceipt();
    const busy = send.isPending || dismiss.isPending;
    return (
        <NoticeBar
            label="Read receipt requested"
            icon={<Mail className="h-4 w-4 text-primary" />}
            actions={
                <>
                    <Button variant="outline" size="sm" className="h-7 bg-card text-xs" onClick={() => dismiss.mutate(messageId)} disabled={busy}>
                        Don't send
                    </Button>
                    <Button variant="default" size="sm" className="h-7 text-xs" onClick={() => send.mutate(messageId)} disabled={busy}>
                        Send receipt
                    </Button>
                </>
            }
        >
            <span className="font-semibold">Read receipt requested.</span> The sender wants to know when you've read
            this — we'll let <span className="font-mono text-xs">{requestedTo}</span> know.
        </NoticeBar>
    );
}

/** Outlook's "Some content in this message has been blocked…" bar. */
function RemoteContentBanner({
    trusting,
    onTrust,
    onShow
}: {
    trusting: boolean;
    onTrust: () => void;
    onShow: () => void;
}) {
    return (
        <NoticeBar
            label="Blocked content"
            icon={<Info className="h-4 w-4 text-muted-foreground" />}
            actions={
                <>
                    <Button size="sm" variant="outline" className="h-7 bg-card text-xs" disabled={trusting} onClick={onTrust}>
                        {trusting ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> : null}
                        Trust sender
                    </Button>
                    <Button size="sm" variant="outline" className="h-7 bg-card text-xs" onClick={onShow}>
                        Show blocked content
                    </Button>
                </>
            }
        >
            Some content in this message has been blocked because the sender isn't in your Safe senders list.
        </NoticeBar>
    );
}
